import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { landingAssetsForLocale } from '../config/landingAssets'

interface Feature {
  imgSrc: string
  title: ReactNode
  description: ReactNode
}

interface Highlight {
  icon: string
  title: string
  description: string
}

function FeatureItem({ feature }: { feature: Feature }) {
  return (
    <div className='w-full max-w-md'>
      <img src={feature.imgSrc} alt='' className='mb-6 w-full rounded-lg' loading='lazy' />
      <div className='space-y-4 text-center'>
        {feature.title && <h2 className='text-gray-12 text-xl font-bold'>{feature.title}</h2>}
        <div className='text-gray-12 tracking-0 space-y-4 text-center text-base font-normal leading-[160%]'>
          {feature.description}
        </div>
      </div>
    </div>
  )
}

function HighlightList({ highlights }: { highlights: Highlight[] }) {
  return (
    <div className='space-y-7'>
      {highlights.map(highlight => (
        <div key={highlight.title} className='flex items-start gap-3'>
          <img src={highlight.icon} alt='' className='mt-1 h-6 w-6 flex-shrink-0' loading='lazy' />
          <div className='text-left'>
            <h3 className='text-gray-12 text-xl font-semibold leading-[140%]'>{highlight.title}</h3>
            <p className='text-gray-12 mt-2 text-base font-normal leading-[160%]'>
              {highlight.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function HeroSectionUs() {
  const { t, i18n } = useTranslation('landingPage')
  const assets = landingAssetsForLocale(i18n.language)
  const highlights: Highlight[] = [
    {
      icon: assets.highlights[0],
      title: t('hero.highlights.0.title'),
      description: t('hero.highlights.0.description'),
    },
    {
      icon: assets.highlights[1],
      title: t('hero.highlights.1.title'),
      description: t('hero.highlights.1.description'),
    },
    {
      icon: assets.highlights[2],
      title: t('hero.highlights.2.title'),
      description: t('hero.highlights.2.description'),
    },
  ]
  const features: Feature[] = [
    {
      imgSrc: assets.featureImages.default[0],
      title: t('hero.features.0.title'),
      description: (
        <>
          <span className='whitespace-pre-line'>{t('hero.features.0.description')}</span>
          <div className='text-[13px]'>{t('hero.features.0.note')}</div>
        </>
      ),
    },
    {
      imgSrc: assets.featureImages.default[1],
      title: null,
      description: <HighlightList highlights={highlights} />,
    },
    {
      imgSrc: assets.featureImages.default[2],
      title: t('hero.features.2.title'),
      description: <span className='whitespace-pre-line'>{t('hero.features.2.description')}</span>,
    },
  ]

  return (
    <section className='px-4 pb-8'>
      <div className='mb-8 text-center'>
        <h2 className='text-gray-12 text-2xl font-semibold'>{t('hero.heading')}</h2>
      </div>

      <div className='flex flex-col items-center justify-center space-y-10'>
        {features.map(feature => (
          <FeatureItem key={feature.imgSrc} feature={feature} />
        ))}
      </div>
    </section>
  )
}
